// IGK-2P v2.1 Phase-3 — Blueprint Board + Scene Look panel (keyframe stage).
// Batch lineart grid + badges · Approve-all-clean · Generate-approved-N ·
// escalation inbox pill · per-scene Scene Look picker (read-only mapping;
// apply = re-grade with a blast-radius notice — re-grade endpoint is
// morning-queued) · Confirm-Lineup per scene. Mounts at the top of the
// keyframe grid (behind REACT_APP_IGK2P_ENABLED).
import React, { useEffect, useState, useCallback } from 'react';
import { LayoutGrid, RefreshCw, CheckCircle2, Loader2, Sparkles, AlertTriangle, ChevronDown, ChevronRight, Palette, ClipboardCheck } from 'lucide-react';
import { toast } from '../../utils/toast';
import { getBlueprints, genBlueprintsBatch, approveBlueprint, regenIGK2P, confirmLineup, applySceneLook, STATE_META, SCENE_LOOK_MAP, resolveLook } from './igk2pApi';

const resolveUrl = (url) => (!url ? '' : url.startsWith('http') ? url : `${window.__API_BASE}${url}`);

export default function Igk2pBlueprintBoard({ projectId, onReload }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applyScene, setApplyScene] = useState(null);
  const [applying, setApplying] = useState(false);

  const load = useCallback(() => {
    getBlueprints(projectId).then(setData).catch(() => {});
  }, [projectId]);

  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => {
    if (!open || !data?.processing) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [open, data?.processing, load]);

  const shots = data?.shots || [];
  const escalations = shots.filter((s) => s.state === 'red' || s.status === 'failed');
  const cleanPending = shots.filter((s) => s.state === 'ready_pending_cd');
  const approvedCount = shots.filter((s) => s.state === 'approved').length;
  const scenes = [...new Set(shots.map((s) => s.scene_number).filter(Boolean))].sort();

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); setTimeout(load, 1000); }
    catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  const approveAllClean = async () => {
    setBusy(true);
    let ok = 0;
    for (const s of cleanPending) {
      try { const r = await approveBlueprint(s.shot_id); if (r.success) ok++; } catch {}
    }
    toast.success(`Approved ${ok}/${cleanPending.length} clean blueprints`);
    setBusy(false); load();
  };

  const generateApproved = async () => {
    const ids = shots.filter((s) => s.state === 'approved').map((s) => s.shot_id);
    if (!ids.length) { toast.message('No approved blueprints to render.'); return; }
    setBusy(true);
    let ok = 0;
    for (const id of ids) { try { await regenIGK2P(id); ok++; } catch {} }
    toast.success(`Queued ${ok}/${ids.length} approved shots for compose`);
    setBusy(false); onReload?.();
  };

  const doConfirmLineup = async (scene) => {
    try {
      const r = await confirmLineup(projectId, scene);
      if (r.ok) toast.success(`Scene ${scene} lineup confirmed (${r.locked_count} locked)`);
      else toast.error(`Lineup blocked — unlocked: ${(r.unlocked || []).join(', ') || 'some shots'}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Confirm lineup failed'); }
  };

  return (
    <div className="mb-4 rounded-xl border border-fuchsia-400/20 bg-gradient-to-br from-fuchsia-500/[0.04] to-amber-500/[0.03]" data-testid="igk2p-blueprint-board">
      <button onClick={() => setOpen((v) => !v)} data-testid="igk2p-board-toggle"
        className="w-full flex items-center gap-2 px-3 py-2 text-left">
        {open ? <ChevronDown className="w-4 h-4 text-white/50" /> : <ChevronRight className="w-4 h-4 text-white/50" />}
        <LayoutGrid className="w-4 h-4 text-fuchsia-300" />
        <span className="text-[12px] font-semibold text-white">IGK-2P · Blueprint Board</span>
        <span className="text-[9px] text-white/40">preview only</span>
        {escalations.length > 0 && (
          <span data-testid="igk2p-escalation-pill" className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 text-[9px] font-semibold">
            <AlertTriangle className="w-3 h-3" /> {escalations.length} escalation{escalations.length === 1 ? '' : 's'}
          </span>
        )}
      </button>

      {open && (
        <div className="px-3 pb-3">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-1.5 mb-3">
            <button data-testid="igk2p-gen-blueprints-batch" disabled={busy} onClick={() => run(() => genBlueprintsBatch(projectId), 'Blueprint batch queued')}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-cyan-500/20 text-cyan-200 text-[10px] hover:bg-cyan-500/30 disabled:opacity-40">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Generate blueprints
            </button>
            <button data-testid="igk2p-approve-all-clean" disabled={busy || !cleanPending.length} onClick={approveAllClean}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/20 text-emerald-200 text-[10px] hover:bg-emerald-500/30 disabled:opacity-30">
              <CheckCircle2 className="w-3 h-3" /> Approve all clean ({cleanPending.length})
            </button>
            <button data-testid="igk2p-generate-approved" disabled={busy || !approvedCount} onClick={generateApproved}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-fuchsia-500/20 text-fuchsia-200 text-[10px] hover:bg-fuchsia-500/30 disabled:opacity-30">
              <Sparkles className="w-3 h-3" /> Generate approved ({approvedCount})
            </button>
            <button data-testid="igk2p-board-refresh" onClick={load} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/5 text-white/60 text-[10px] hover:bg-white/10">
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
            {data?.processing && <span className="text-[9px] text-amber-300 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> generating…</span>}
          </div>

          {/* Lineart grid */}
          {shots.length === 0 ? (
            <div className="text-[10px] text-white/40 py-4 text-center">No blueprint data yet — click “Generate blueprints”.</div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="igk2p-board-grid">
              {shots.map((s) => {
                const stKey = s.state === 'approved' ? 'green' : s.state === 'ready_pending_cd' ? 'amber' : s.state === 'stale' || s.status === 'failed' ? 'red' : s.status === 'ready' ? 'amber' : 'grey';
                const meta = STATE_META[stKey];
                return (
                  <div key={s.shot_id} className="rounded-md border border-white/10 bg-black/30 overflow-hidden" data-testid={`igk2p-board-card-${s.shot_id}`}>
                    {s.blueprint_url ? <img src={resolveUrl(s.blueprint_url)} alt="bp" className="w-full aspect-video object-contain bg-white" />
                      : <div className="w-full aspect-video bg-white/5 flex items-center justify-center text-[8px] text-white/30">no lineart</div>}
                    <div className="flex items-center gap-1 px-1.5 py-1">
                      <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
                      <span className="text-[9px] text-white/70">{s.scene_number}-{s.shot_number}</span>
                      {s.ink_density != null && <span className="ml-auto text-[8px] text-white/30">{(s.ink_density * 100).toFixed(1)}%</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Scene Look panel */}
          <div className="mt-4 rounded-lg border border-white/10 bg-black/30 p-3" data-testid="igk2p-scene-look-panel">
            <div className="flex items-center gap-2 mb-2">
              <Palette className="w-3.5 h-3.5 text-fuchsia-300" />
              <span className="text-[11px] font-semibold text-white">Scene Look</span>
              <span className="text-[9px] text-white/40">apply = re-grade only (zero regen)</span>
            </div>
            <div className="space-y-1.5">
              {scenes.map((sc) => {
                const look = resolveLook(sc);
                const count = shots.filter((s) => s.scene_number === sc).length;
                return (
                  <div key={sc} className="flex items-center gap-2 text-[10px]" data-testid={`igk2p-scene-row-${sc}`}>
                    <span className="text-white/70 w-12">{sc}</span>
                    <span className={`px-1.5 py-0.5 rounded ${look === 'AU_Dusk' ? 'bg-indigo-500/20 text-indigo-200' : 'bg-amber-500/15 text-amber-200'} border border-white/10`}>{look}</span>
                    <span className="text-white/30">{count} shot{count === 1 ? '' : 's'}</span>
                    <button data-testid={`igk2p-apply-look-${sc}`} onClick={() => setApplyScene({ scene: sc, look, count })}
                      className="ml-auto px-2 py-0.5 rounded bg-white/5 text-white/60 hover:bg-white/10">Apply look</button>
                    <button data-testid={`igk2p-confirm-lineup-${sc}`} onClick={() => doConfirmLineup(sc)}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25"><ClipboardCheck className="w-3 h-3" /> Confirm lineup</button>
                  </div>
                );
              })}
              {scenes.length === 0 && <span className="text-[9px] text-white/30">No scenes.</span>}
            </div>
          </div>
        </div>
      )}

      {/* Apply-look blast-radius confirm */}
      {applyScene && (
        <div className="fixed inset-0 z-[210] bg-black/80 flex items-center justify-center p-4" data-testid="igk2p-apply-look-modal" onClick={() => setApplyScene(null)}>
          <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0c0c10] p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-2"><Palette className="w-4 h-4 text-fuchsia-300" /><span className="text-sm font-semibold text-white">Apply {applyScene.look} → {applyScene.scene}</span></div>
            <div className="text-[11px] text-amber-200/90 bg-amber-500/10 border border-amber-400/30 rounded-md px-2 py-1.5 mb-3">
              <AlertTriangle className="w-3 h-3 inline mr-1" /> Blast radius: all <b>{applyScene.count}</b> shot(s) in {applyScene.scene} re-grade from their conformed masters. No pixels are regenerated.
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setApplyScene(null)} className="flex-1 px-3 py-1.5 rounded-md bg-white/5 text-white/60 text-[10px]" data-testid="igk2p-apply-cancel">Cancel</button>
              <button data-testid="igk2p-apply-confirm" disabled={applying}
                onClick={async () => {
                  setApplying(true);
                  try {
                    const r = await applySceneLook(projectId, applyScene.scene, applyScene.look);
                    toast.success(`Re-graded ${r.regraded_count} shot(s) → ${r.look_title} · 0 regenerations${r.skipped_locked?.length ? ` · ${r.skipped_locked.length} locked skipped` : ''}`);
                    setApplyScene(null); onReload?.();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Re-grade failed');
                  } finally { setApplying(false); }
                }}
                className="flex-1 px-3 py-1.5 rounded-md bg-fuchsia-500/25 text-fuchsia-100 text-[10px] font-semibold disabled:opacity-50">{applying ? 'Re-grading…' : 'Apply re-grade'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
